import { Controller } from '@hotwired/stimulus'
import { S3DirectUpload } from '../lib/s3_direct_upload'

export default class extends Controller {
  static targets = ['input', 'dropZone', 'uploadGrid', 'emptyState']
  static classes = ['dragging']
  static values = {
    directory: String,
    policy: String,
  }

  connect() {
    this.bindEvents()
  }

  bindEvents() {
    // Prevent default drag behaviors
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach((eventName) => {
      this.dropZoneTarget.addEventListener(eventName, this.preventDefaults, false)
    });

    // Handle drag states
    ['dragenter', 'dragover'].forEach((eventName) => {
      this.dropZoneTarget.addEventListener(eventName, this.highlight.bind(this))
    });

    ['dragleave', 'drop'].forEach((eventName) => {
      this.dropZoneTarget.addEventListener(eventName, this.unhighlight.bind(this))
    })

    // Handle drops
    this.dropZoneTarget.addEventListener('drop', this.handleDrop.bind(this))
  }

  preventDefaults(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  highlight() {
    this.dropZoneTarget.classList.add(this.draggingClass)
  }

  unhighlight() {
    this.dropZoneTarget.classList.remove(this.draggingClass)
  }

  async handleDrop(e) {
    const files = e.dataTransfer.files
    this.uploadFiles(files)
  }

  async handleFileSelect(e) {
    const files = e.target.files
    this.uploadFiles(files)
    this.inputTarget.value = '' // Reset input for future uploads
  }

  async uploadFiles(files) {
    // Hide empty state if we have files
    this.emptyStateTarget.classList.add('hidden')

    for (const file of files) {
      const uploadItem = this.createUploadItem(file)
      this.uploadGridTarget.appendChild(uploadItem)

      try {
        const uploader = new S3DirectUpload({
          file,
          extraParams: {
            directory: this.directoryValue,
            policy: this.policyValue,
          },
          onProgress: (progress) => {
            this.updateItemProgress(uploadItem, progress)
          },
          onSuccess: (result) => {
            this.handleItemSuccess(uploadItem, result)
          },
          onError: (error) => {
            this.handleItemError(uploadItem, error)
          },
        })

        await uploader.upload()
      }
      catch (error) {
        console.error('Upload failed:', error)
      }
    }
  }

  updateProgress(progress) {
    if (this.hasProgressTarget) {
      this.progressTarget.style.width = `${progress}%`
    }
  }

  handleUploadSuccess(result) {
    // Dispatch custom event with upload result
    console.log(result)
    const event = new CustomEvent('upload:success', {
      detail: result,
      bubbles: true,
    })
    this.element.dispatchEvent(event)

    // Clear the input
    this.inputTarget.value = ''
  }

  handleUploadError(error) {
    const event = new CustomEvent('upload:error', {
      detail: error,
      bubbles: true,
    })
    this.element.dispatchEvent(event)
  }

  createUploadItem(file) {
    const div = document.createElement('div')
    div.className = 'relative aspect-square rounded-lg border border-gray-300 overflow-hidden'
    div.innerHTML = `
      <div class="absolute inset-0 bg-gray-100 flex items-center justify-center">
        <div class="text-sm text-gray-500">${file.name}</div>
      </div>
      <div class="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
        <div class="h-full bg-indigo-600 transition-all duration-300" style="width: 0%"></div>
      </div>
    `
    return div
  }

  updateItemProgress(item, progress) {
    const progressBar = item.querySelector('.bg-indigo-600')
    if (progressBar) {
      progressBar.style.width = `${progress}%`
    }
  }

  handleItemSuccess(item, result) {
    // Replace placeholder with image
    item.innerHTML = `
      <div class="relative group aspect-square">
        <img src="${result.url}" alt="" class="h-full w-full object-cover rounded-lg">
        <button type="button" 
          class="absolute invisible group-hover:visible top-1 right-1 p-1 rounded-full bg-gray-900/50 text-white hover:bg-gray-900/75"
          data-action="click->issues-upload#removeItem">
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
        <input type="hidden" name="issue[image_keys][]" value="${result.key}">
      </div>
    `
  }

  handleItemError(item, error) {
    item.innerHTML = `
      <div class="absolute inset-0 bg-red-50 flex items-center justify-center">
        <div class="text-sm text-red-500">Upload failed ${error}</div>
      </div>
    `
  }

  removeItem(e) {
    const item = e.target.closest('.aspect-square')
    item.remove()

    // Show empty state if no more uploads
    if (this.uploadGridTarget.children.length === 0) {
      this.emptyStateTarget.classList.remove('hidden')
    }
  }
}
